import { readHomeCountryChoice } from '@peekapak/country-origin';
import { residencyForHomeCountry, type DataResidency } from '@peekapak/common/lib/regionAndResidency';

export enum SupportedDataResidency {
  USA = 'USA',
  ARE = 'ARE',
}

/**
 * THE CROSS-APP CONTRACT.
 *
 * peekapak.com and peekapak.com/play are the same origin, and the embedded
 * myPeekaville build picks the parent's session up entirely through browser
 * state: this key says WHICH Cognito pool's cookies to look for, and the
 * cookies themselves are written by Amplify under
 * `CognitoIdentityServiceProvider.<clientId>.*` (clientId differs per
 * residency). Nothing else is handed over - no query params, no postMessage.
 *
 * The two apps deploy on independent cadences, so the WRITE side of this
 * contract is frozen: the key name, and the values 'USA' | 'ARE'. Changing
 * either breaks every peer build already in the wild until it is redeployed -
 * which is exactly the failure this module exists to prevent (MYP-846: a /play
 * bundle still on the pre-PE-1346 'us'/'uae' enum could not read the parent's
 * 'ARE', fell back to the US pool, and found no session).
 *
 * The READ side is deliberately forgiving in both directions - see
 * `resolveStoredDataResidency`.
 */
export const DATA_RESIDENCY_STORAGE_KEY = 'peekapak.userDataResidency';

/** Bridge the canonical `DataResidency` union to the nominal enum. */
export function toSupportedResidency(dataResidency: DataResidency): SupportedDataResidency {
  return dataResidency === 'ARE' ? SupportedDataResidency.ARE : SupportedDataResidency.USA;
}

// Values written by builds older than PE-1346 (2026-07-09), which are still in
// browsers that have not signed in since. Kept indefinitely: a stored residency
// is only rewritten on an authoritative event, so an account that has not
// re-authenticated can carry one of these for a very long time.
const LEGACY_STORED_VALUES: Record<string, SupportedDataResidency> = {
  us: SupportedDataResidency.USA,
  uae: SupportedDataResidency.ARE,
};

const unrecognisedValuesWarned = new Set<string>();

/**
 * Resolve a stored residency string, or null when there is nothing usable.
 *
 * Tolerant on purpose, because the writer may be a peer app on a different
 * deploy: legacy lowercase values migrate, and case is canonicalised so a value
 * differing only in case is never mistaken for an unknown one.
 *
 * An unrecognised value returns null - "unknown", not "absent". Callers must
 * not read null as licence to force USA; see `resolvePersistedDataResidency`.
 * It is also warned about by name, once, so the next version skew shows up as a
 * single readable line instead of having to be inferred from a Cognito clientId
 * in a debug log.
 */
export function resolveStoredDataResidency(storedValue: string | null | undefined): SupportedDataResidency | null {
  if (!storedValue) {
    return null;
  }

  const trimmed = storedValue.trim();
  if (!trimmed) {
    return null;
  }

  const legacy = LEGACY_STORED_VALUES[trimmed.toLowerCase()];
  if (legacy) {
    return legacy;
  }

  const canonical = trimmed.toUpperCase();
  if (Object.values(SupportedDataResidency).includes(canonical as SupportedDataResidency)) {
    return canonical as SupportedDataResidency;
  }

  if (!unrecognisedValuesWarned.has(trimmed)) {
    unrecognisedValuesWarned.add(trimmed);
    console.warn(
      `[data-residency] Ignoring unrecognised stored residency ${JSON.stringify(
        trimmed,
      )}. This build may be older than the one that wrote it; treating the ` +
        'residency as unknown rather than guessing.',
    );
  }

  return null;
}

/**
 * The authoritative residency for this browser, or null when none is known.
 *
 * Precedence, highest first:
 *   1. the user's explicit home-country choice (`peekapak.homeCountryChoice`)
 *   2. a residency stored by a prior authoritative event - an explicit choice,
 *      or a successful sign-in/sign-up/QR/OAuth
 *
 * This is the single place either app answers "which region is this session?"
 * outside of live detection. Reading `localStorage` directly at a call site is
 * what produced MYP-846: the reactivation path did its own raw read plus an
 * `Object.values(enum).includes()` test, so it saw neither the legacy-value
 * migration nor the home-country choice, and a value it did not recognise was
 * indistinguishable from no value at all.
 *
 * Returns null rather than defaulting, so callers decide explicitly whether to
 * detect, prompt, or try regions in turn.
 */
export function resolvePersistedDataResidency(): SupportedDataResidency | null {
  const explicitChoice = readHomeCountryChoice();
  if (explicitChoice) {
    return toSupportedResidency(residencyForHomeCountry(explicitChoice));
  }

  return resolveStoredDataResidency(readStoredDataResidency());
}

/** The raw stored string, unresolved. Prefer `resolvePersistedDataResidency`. */
export function readStoredDataResidency(): string | null {
  try {
    return localStorage.getItem(DATA_RESIDENCY_STORAGE_KEY);
  } catch (error) {
    // Private-mode browsers can reject storage access entirely.
    console.warn('[data-residency] Could not read stored residency:', error);
    return null;
  }
}

/** Test seam: forget which unrecognised values have already been warned about. */
export function resetUnrecognisedResidencyWarningsForTests(): void {
  unrecognisedValuesWarned.clear();
}
